import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Circle, Flag, User, Calendar, Tag, Type, Layers, Layout, X } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import type { FilterFieldType } from '../../types';

const FIELD_DEFS: { field: FilterFieldType; Icon: React.FC<{ className?: string }> }[] = [
  { field: 'status', Icon: Circle },
  { field: 'priority', Icon: Flag },
  { field: 'assignee', Icon: User },
  { field: 'dueDate', Icon: Calendar },
  { field: 'tag', Icon: Tag },
  { field: 'title', Icon: Type },
  { field: 'group', Icon: Layers },
  { field: 'board', Icon: Layout },
  { field: 'startDate', Icon: Calendar },
];

interface FieldPickerMenuProps {
  usedFields: FilterFieldType[];
  onSelect: (field: FilterFieldType) => void;
  onClose: () => void;
}

export function FieldPickerMenu({ usedFields, onSelect, onClose }: FieldPickerMenuProps) {
  const { t } = useTranslation('tasks');
  const [search, setSearch] = useState('');

  const available = FIELD_DEFS.filter(
    ({ field }) =>
      !usedFields.includes(field) &&
      t(`filterPanel.fields.${field}`).toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex flex-col" data-testid="tasks-filter-field-picker">
      <div className="p-2">
        <Input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('filterPanel.searchField')}
          className="h-7 text-xs"
          data-testid="tasks-filter-field-picker-search"
        />
      </div>

      <div className="flex flex-col py-1">
        {available.length === 0 ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">{t('filterPanel.noMoreFields')}</p>
        ) : (
          available.map(({ field, Icon }) => (
            <button
              type="button"
              key={field}
              data-testid={`tasks-filter-field-picker-${field}`}
              onClick={() => onSelect(field)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent"
            >
              <Icon className="size-4 text-muted-foreground" />
              {t(`filterPanel.fields.${field}`)}
            </button>
          ))
        )}
      </div>

      <div className="border-t p-2">
        <button
          type="button"
          data-testid="tasks-filter-field-picker-cancel"
          onClick={onClose}
          className="flex items-center gap-2 px-1 py-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <X className="size-3.5" />
          {t('filterPanel.cancel')}
        </button>
      </div>
    </div>
  );
}
